import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { useAppLoading } from "./context/AppLoadingContext";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { PhoneRepairRequiredRoute } from "./routes/PhoneRepairRequiredRoute";
import { PublicRoute } from "./routes/PublicRoute";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { RankingPage } from "./pages/RankingPage";
import { PredictionsPage } from "./pages/PredictionsPage";
import { ScanQrPage } from "./pages/ScanQrPage";
import { AdminShell } from "./components/admin/AdminShell";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminQrPage } from "./pages/AdminQrPage";
import { AdminPredictionAuditPage } from "./pages/AdminPredictionAuditPage";
import { RegisterPage } from "./pages/RegisterPage";
import { PhoneRepairPage } from "./pages/PhoneRepairPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ForgotUsernamePage } from "./pages/ForgotUsernamePage";
import { GlobalLoader } from "./components/ui/GlobalLoader";
import { ClosingPage } from "./pages/ClosingPage";

const siteMode = String(import.meta.env.VITE_SITE_MODE ?? "app").trim().toLowerCase();
const isClosingMode = siteMode === "closing" || siteMode === "static";

function App() {
  if (isClosingMode) {
    return <ClosingPage />;
  }

  const { status: authStatus } = useAuth();
  const { isBlocking } = useAppLoading();
  const location = useLocation();
  const [windowLoaded, setWindowLoaded] = useState(
    typeof document !== "undefined" && document.readyState === "complete",
  );
  const [minVisibleElapsed, setMinVisibleElapsed] = useState(false);
  const [routeTransitionLoading, setRouteTransitionLoading] = useState(false);
  const [fontsReady, setFontsReady] = useState(
    typeof document === "undefined" || !document.fonts ? true : document.fonts.status === "loaded",
  );
  const isFirstRouteRenderRef = useRef(true);

  useEffect(() => {
    if (document.readyState === "complete") {
      setWindowLoaded(true);
      return undefined;
    }

    const handleLoad = () => {
      setWindowLoaded(true);
    };

    window.addEventListener("load", handleLoad, { once: true });

    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMinVisibleElapsed(true);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useLayoutEffect(() => {
    if (isFirstRouteRenderRef.current) {
      isFirstRouteRenderRef.current = false;
      return undefined;
    }

    setRouteTransitionLoading(true);

    const timeoutId = window.setTimeout(() => {
      setRouteTransitionLoading(false);
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [location.key]);

  useEffect(() => {
    if (!document.fonts?.ready) {
      setFontsReady(true);
      return undefined;
    }

    let isMounted = true;

    void document.fonts.ready.finally(() => {
      if (isMounted) {
        setFontsReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const showLoader =
    authStatus === "loading" ||
    !windowLoaded ||
    !fontsReady ||
    isBlocking ||
    routeTransitionLoading ||
    !minVisibleElapsed;

  return (
    <>
      <Routes>
        <Route element={<PublicRoute />}>
          <Route path="/" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/forgot-username" element={<ForgotUsernamePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>
        <Route element={<ProtectedRoute redirectTo="/" />}>
          <Route path="/phone-repair" element={<PhoneRepairPage />} />
          <Route element={<PhoneRepairRequiredRoute />}>
            <Route element={<AdminShell />}>
              <Route path="/home" element={<HomePage />} />
              <Route path="/ranking" element={<RankingPage />} />
              <Route path="/predictions" element={<PredictionsPage />} />
              <Route path="/scan-qr" element={<ScanQrPage />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin", "superadmin"]} redirectTo="/home" />
                }
              >
                <Route index element={<Navigate to="/admin/users" replace />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="qr" element={<AdminQrPage />} />
                <Route path="puntajes" element={<AdminPredictionAuditPage />} />
                <Route path="auditoria" element={<Navigate to="/admin/puntajes" replace />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace preventScrollReset />} />
      </Routes>
      <GlobalLoader visible={showLoader} />
    </>
  );
}

export default App;
